import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const TIPOS = [
  { id: 'resumen',       label: '📊 Resumen General' },
  { id: 'viajes',        label: '🚚 Viajes' },
  { id: 'por-conductor', label: '👤 Por Conductor' },
  { id: 'por-vehiculo',  label: '🚛 Por Vehículo' },
  { id: 'por-cliente',   label: '🏢 Por Cliente' },
  { id: 'excedentes',    label: '💰 Excedentes' },
];

const fmtRD = (n) => 'RD$ ' + parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });
const fmtGal = (n) => parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 }) + ' gal';
const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-DO') : '-';

const Reportes = ({ usuario, onLogout }) => {
  const [tipo, setTipo] = useState('resumen');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [conductorId, setConductorId] = useState('');
  const [vehiculoId, setVehiculoId] = useState('');
  const [conductores, setConductores] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [descargando, setDescargando] = useState('');

  // Cargar catálogos para los filtros
  useEffect(() => {
    (async () => {
      try {
        const [cRes, vRes] = await Promise.all([
          api.get('/conductores'),
          api.get('/vehiculos'),
        ]);
        setConductores(cRes.data);
        setVehiculos(vRes.data);
      } catch { /* silencioso */ }
    })();
  }, []);

  const buildParams = useCallback(() => {
    const p = {};
    if (desde) p.desde = desde;
    if (hasta) p.hasta = hasta;
    if (conductorId && tipo === 'viajes') p.conductor_id = conductorId;
    if (vehiculoId && tipo === 'viajes') p.vehiculo_id = vehiculoId;
    return p;
  }, [desde, hasta, conductorId, vehiculoId, tipo]);

  const generar = useCallback(async () => {
    setLoading(true); setError(''); setData(null);
    try {
      const res = await api.get(`/reportes/${tipo}`, { params: buildParams() });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error generando el reporte');
    } finally {
      setLoading(false);
    }
  }, [tipo, buildParams]);

  // Generar al cambiar de tipo (limpiar data primero para evitar render con datos del tipo anterior)
  useEffect(() => { setData(null); generar(); }, [tipo]); // eslint-disable-line

  const descargar = async (formato) => {
    setDescargando(formato); setError('');
    try {
      const params = { tipo, ...buildParams() };
      const res = await api.get(`/reportes/exportar/${formato}`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-${tipo}-${new Date().toISOString().slice(0, 10)}.${formato === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(`Error descargando ${formato.toUpperCase()}`);
    } finally {
      setDescargando('');
    }
  };

  return (
    <Layout usuario={usuario} onLogout={onLogout} title="Reportes">
      {/* Selector de tipo */}
      <div style={S.tipoRow}>
        {TIPOS.map(t => (
          <button
            key={t.id}
            style={{ ...S.tipoBtn, ...(tipo === t.id ? S.tipoBtnActive : {}) }}
            onClick={() => setTipo(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div style={S.filtros}>
        <div style={S.filtroGroup}>
          <label style={S.filtroLabel}>Desde</label>
          <input style={S.input} type="date" value={desde} onChange={e => setDesde(e.target.value)} />
        </div>
        <div style={S.filtroGroup}>
          <label style={S.filtroLabel}>Hasta</label>
          <input style={S.input} type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
        </div>
        {tipo === 'viajes' && (
          <>
            <div style={S.filtroGroup}>
              <label style={S.filtroLabel}>Conductor</label>
              <select style={S.input} value={conductorId} onChange={e => setConductorId(e.target.value)}>
                <option value="">Todos</option>
                {conductores.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div style={S.filtroGroup}>
              <label style={S.filtroLabel}>Vehículo</label>
              <select style={S.input} value={vehiculoId} onChange={e => setVehiculoId(e.target.value)}>
                <option value="">Todos</option>
                {vehiculos.map(v => <option key={v.id} value={v.id}>{v.placa}</option>)}
              </select>
            </div>
          </>
        )}
        <button style={S.btnGenerar} onClick={generar} disabled={loading}>
          {loading ? 'Generando...' : '🔍 Generar'}
        </button>
      </div>

      {/* Botones de descarga */}
      <div style={S.descargaRow}>
        <button style={S.btnPdf} onClick={() => descargar('pdf')} disabled={descargando === 'pdf'}>
          {descargando === 'pdf' ? 'Descargando...' : '📄 PDF'}
        </button>
        <button style={S.btnExcel} onClick={() => descargar('excel')} disabled={descargando === 'excel'}>
          {descargando === 'excel' ? 'Descargando...' : '📊 Excel'}
        </button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Resultados */}
      {loading ? (
        <div style={S.loading}>Cargando...</div>
      ) : !data ? (
        <div style={S.empty}><div style={{ fontSize: '48px' }}>📊</div><p>Selecciona filtros y genera un reporte.</p></div>
      ) : (
        <div style={S.resultado}>
          {tipo === 'resumen' && <ResumenView data={data} />}
          {tipo === 'viajes' && <ViajesView data={data} />}
          {tipo === 'por-conductor' && <ConductorView data={data} />}
          {tipo === 'por-vehiculo' && <VehiculoView data={data} />}
          {tipo === 'por-cliente' && <ClienteView data={data} />}
          {tipo === 'excedentes' && <ExcedentesView data={data} />}
        </div>
      )}
    </Layout>
  );
};

// ─── VISTAS POR TIPO ──────────────────────────────────────────────────────────

const ResumenView = ({ data }) => {
  if (!data) return null;
  return (
  <div>
    <SeccionTitulo>🚚 Viajes</SeccionTitulo>
    <div style={S.cardsRow}>
      <Stat n={data.viajes?.total} l="Total viajes" />
      <Stat n={data.viajes?.completados} l="Completados" />
      <Stat n={data.viajes?.en_curso} l="En curso" />
      <Stat n={fmtGal(data.viajes?.galones_reales)} l="Galones reales" />
    </div>
    <SeccionTitulo>💵 Ventas</SeccionTitulo>
    <div style={S.cardsRow}>
      <Stat n={fmtRD(data.ventas?.total_facturado)} l="Total facturado" rojo />
      <Stat n={fmtGal(data.ventas?.galones_entregados)} l="Galones entregados" />
    </div>
    <SeccionTitulo>💰 Excedentes</SeccionTitulo>
    <div style={S.cardsRow}>
      <Stat n={fmtRD(data.excedentes?.total_generado)} l="Total generado" />
      <Stat n={fmtRD(data.excedentes?.monto_conductor)} l="Parte conductor" />
      <Stat n={fmtRD(data.excedentes?.monto_empresa)} l="Parte empresa" />
      <Stat n={fmtRD(data.excedentes?.pendiente_cobro)} l="Pendiente cobro" rojo />
    </div>
  </div>
  );
};

const ViajesView = ({ data }) => {
  if (!data) return null;
  return (
  <Tabla
    headers={['Fecha', 'Conductor', 'Vehículo', 'Origen', 'Combustible', 'Gal.Decl', 'Gal.Real', 'Gal.Entr', 'Facturado', 'Estado']}
    rows={(Array.isArray(data.viajes) ? data.viajes : []).map(v => [
      fmtFecha(v.fecha_salida), v.conductor, v.vehiculo, v.origen, v.tipo_combustible,
      parseFloat(v.galones_declarados).toFixed(0), parseFloat(v.galones_reales).toFixed(0),
      parseFloat(v.galones_entregados).toFixed(0), fmtRD(v.total_facturado), v.estado,
    ])}
    vacio={data.total_registros === 0}
  />
  );
};

const ConductorView = ({ data }) => {
  if (!data) return null;
  return (
  <Tabla
    headers={['Conductor', 'Viajes', 'Galones', 'Excedente Ganado', 'Pagos', 'Pendiente']}
    rows={(Array.isArray(data.conductores) ? data.conductores : []).map(c => [
      c.conductor, c.viajes, fmtGal(c.galones_transportados),
      fmtRD(c.excedente_ganado), fmtRD(c.pagos_recibidos), fmtRD(c.excedente_pendiente),
    ])}
    vacio={data.total_conductores === 0}
  />
  );
};

const VehiculoView = ({ data }) => {
  if (!data) return null;
  return (
  <Tabla
    headers={['Vehículo', 'Descripción', 'Viajes', 'Galones', 'Facturado']}
    rows={(Array.isArray(data.vehiculos) ? data.vehiculos : []).map(v => [
      v.vehiculo, v.descripcion, v.viajes, fmtGal(v.galones_transportados), fmtRD(v.total_facturado),
    ])}
    vacio={data.total_vehiculos === 0}
  />
  );
};

const ClienteView = ({ data }) => {
  if (!data) return null;
  return (
  <Tabla
    headers={['Cliente', 'Entregas', 'Galones', 'Total Facturado']}
    rows={(Array.isArray(data.clientes) ? data.clientes : []).map(c => [
      c.cliente, c.entregas, fmtGal(c.galones), fmtRD(c.total_facturado),
    ])}
    vacio={data.total_clientes === 0}
  />
  );
};

const ExcedentesView = ({ data }) => {
  if (!data) return null;
  return (
  <div>
    <div style={S.cardsRow}>
      <Stat n={fmtRD(data.totales?.generado)} l="Generado" />
      <Stat n={fmtRD(data.totales?.pagado)} l="Pagado" />
      <Stat n={fmtRD(data.totales?.pendiente)} l="Pendiente" rojo />
    </div>
    <Tabla
      headers={['Fecha', 'Conductor', 'Gal.Exc', 'Total', 'Conductor', 'Empresa', 'Pagado', 'Pendiente', 'Estado']}
      rows={(Array.isArray(data.excedentes) ? data.excedentes : []).map(e => [
        fmtFecha(e.fecha), e.conductor, parseFloat(e.galones_excedente).toFixed(0),
        fmtRD(e.total_excedente), fmtRD(e.monto_conductor), fmtRD(e.monto_empresa),
        fmtRD(e.pagado), fmtRD(e.pendiente), e.estado,
      ])}
      vacio={data.total_registros === 0}
    />
  </div>
  );
};

// ─── COMPONENTES REUTILIZABLES ────────────────────────────────────────────────

const Stat = ({ n, l, rojo }) => (
  <div style={S.stat}>
    <div style={{ ...S.statNum, ...(rojo ? { color: '#CC0000' } : {}) }}>{n}</div>
    <div style={S.statLabel}>{l}</div>
  </div>
);

const SeccionTitulo = ({ children }) => <h3 style={S.seccionTitulo}>{children}</h3>;

const Tabla = ({ headers, rows, vacio }) => {
  if (vacio) return <div style={S.empty}><p>No hay datos para el período seleccionado.</p></div>;
  return (
    <div style={S.tablaWrap}>
      <table style={S.tabla}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={ri % 2 ? S.trAlt : {}}>
              {row.map((cell, ci) => <td key={ci} style={{ ...S.td, ...(ci === 0 ? {} : S.tdRight) }}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const S = {
  tipoRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' },
  tipoBtn: { background: '#fff', border: '1.5px solid #e0e0e0', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', color: '#666', cursor: 'pointer' },
  tipoBtnActive: { background: '#CC0000', color: '#fff', borderColor: '#CC0000' },
  filtros: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e0e0e0', marginBottom: '12px' },
  filtroGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filtroLabel: { fontSize: '11px', color: '#888', fontWeight: '600' },
  input: { padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #e0e0e0', fontSize: '13px', outline: 'none', color: '#1a1a1a' },
  btnGenerar: { background: '#CC0000', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', height: '36px' },
  descargaRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
  btnPdf: { background: '#fff', border: '1.5px solid #CC0000', color: '#CC0000', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnExcel: { background: '#fff', border: '1.5px solid #0a7d33', color: '#0a7d33', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  error: { background: '#fff0f0', border: '1px solid #ffcccc', color: '#CC0000', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  loading: { textAlign: 'center', padding: '60px', color: '#666' },
  empty: { textAlign: 'center', padding: '50px', color: '#999', background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0' },
  resultado: { marginTop: '4px' },
  cardsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '10px', marginBottom: '16px' },
  stat: { background: '#fff', borderRadius: '12px', border: '1.5px solid #e0e0e0', padding: '16px', textAlign: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  statNum: { fontSize: '20px', fontWeight: '800', color: '#1a1a1a', lineHeight: 1.1 },
  statLabel: { fontSize: '11px', color: '#888', marginTop: '5px', fontWeight: '500' },
  seccionTitulo: { fontSize: '14px', fontWeight: '700', color: '#1a1a1a', margin: '18px 0 10px' },
  tablaWrap: { background: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', overflow: 'auto', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { background: '#CC0000', color: '#fff', padding: '10px 8px', textAlign: 'left', fontWeight: '700', whiteSpace: 'nowrap' },
  td: { padding: '8px', borderBottom: '1px solid #f0f0f0', color: '#1a1a1a', whiteSpace: 'nowrap' },
  tdRight: { textAlign: 'right' },
  trAlt: { background: '#fafafa' },
};

export default Reportes;
